import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, CheckCircle, FileText, Shield, TrendingDown } from 'lucide-react';

interface EnvironnementPartageModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function EnvironnementPartageModal({ isOpen, onClose }: EnvironnementPartageModalProps) {
  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="bg-gradient-to-br from-slate-900 via-green-900 to-slate-900 rounded-2xl max-w-5xl w-full my-8 border border-green-500/30 shadow-2xl shadow-green-500/20"
          >
            {/* Header */}
            <div className="relative p-8 border-b border-green-500/30">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl">
                    <Share2 className="w-10 h-10 text-white" />
                  </div>
                  <div>
                    <h2 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                      Environnement Partagé (IAAS)
                    </h2>
                    <p className="text-gray-400 mt-1 text-lg">
                      Solution cloud économique avec gestion complète par BZ - SPLA autorisé
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-8 space-y-8 max-h-[calc(100vh-300px)] overflow-y-auto">
              
              <div className="bg-gradient-to-r from-green-600/20 via-emerald-600/20 to-green-600/20 rounded-xl p-8 border-2 border-green-500/50">
                <h3 className="text-2xl font-bold text-center text-white mb-4">
                  Environnement Partagé : Solution Économique
                </h3>
                <p className="text-gray-300 text-center text-lg">
                  L'environnement partagé est notre formule <strong className="text-white">IAAS</strong> (Infrastructure as a Service). 
                  Nous partageons des serveurs pour plusieurs clients, ce qui rend la solution <strong className="text-green-400">plus économique</strong>. 
                  BZ gère l'<strong className="text-emerald-400">hypervisor</strong> et les <strong className="text-emerald-400">VM</strong>, 
                  vous vous concentrez sur vos applications.
                </p>
              </div>

              <div className="space-y-6">
                <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <TrendingDown className="w-7 h-7 text-green-400" />
                    Avantages Économiques
                  </h4>
                  <p className="text-gray-300 mb-4">
                    L'environnement partagé offre plusieurs avantages économiques :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Coûts réduits</strong> - Partage des ressources serveur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Pas d'investissement initial</strong> - Pas besoin d'acheter du matériel</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Facturation flexible</strong> - Payez selon vos besoins</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Scalabilité</strong> - Augmentez ou diminuez selon vos besoins</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-emerald-500/10 to-green-500/10 rounded-xl p-6 border border-emerald-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-emerald-400" />
                    Responsabilités BZ
                  </h4>
                  <p className="text-gray-300 mb-4">
                    En environnement partagé, BZ Inc. a la responsabilité complète de l'infrastructure :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Hypervisor</strong> - Gestion complète de l'hyperviseur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Machines Virtuelles (VM)</strong> - Création, gestion, maintenance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Infrastructure physique</strong> - Matériel, réseau, redondance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Sécurité</strong> - Protection, monitoring, accès</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl p-6 border border-blue-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-7 h-7 text-blue-400" />
                    SPLA Autorisé avec Microsoft
                  </h4>
                  <p className="text-gray-300 mb-4">
                    BZ Inc. est <strong className="text-white">SPLA autorisé</strong> avec Microsoft. Nous avons une 
                    <strong className="text-white"> rigueur sur la gestion des licences</strong> :
                  </p>
                  <ul className="space-y-2 text-gray-300">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Procédures de validation</strong> en place</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Audits réguliers</strong> pour garantir la conformité</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Rapports détaillés</strong> sur l'utilisation des licences</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-white">Conformité garantie</strong> avec les exigences Microsoft</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl p-6 border border-purple-500/30">
                  <h4 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-7 h-7 text-purple-400" />
                    Accès Sécurisés
                  </h4>
                  <p className="text-gray-300">
                    Les accès sont <strong className="text-white">sécurisés</strong> tant au niveau <strong className="text-white">physique</strong> que 
                    <strong className="text-white"> virtuel</strong>. Contrôles d'accès stricts, monitoring continu, protection complète de votre infrastructure.
                  </p>
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="p-6 border-t border-green-500/30 bg-slate-900/50">
              <div className="flex items-center justify-end">
                <button
                  onClick={onClose}
                  className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl hover:from-green-600 hover:to-emerald-600 transition-all duration-200 font-semibold shadow-lg shadow-green-500/20 hover:shadow-green-500/40 hover:scale-105"
                >
                  Fermer
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}












